import { type ClassValue, clsx } from 'clsx';
import { useSyncExternalStore } from 'react';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/*
 * The product's size (Kazzy's UX rule 11). `--dw-scale` sets the root font size (tokens.css, app.css) and the build
 * turns every CSS px into rem (scripts/px-to-rem.mjs), so all CSS follows it. A length written in JS does not pass
 * through the build, so it reads the same scale here:
 *  - a measured length (getBoundingClientRect, clientWidth) is already real and stays as it is;
 *  - a design length JS only sets (a face's 32px) is written `rem(32)`, a CSS length that follows the root by itself;
 *  - a design length JS computes with (the toolbar's edge, a chart's plot) goes through `scaled(px, useRootScale())`;
 *  - a custom property read from a style (`--dw-top-room`, now "5.75rem") goes through `cssLengthPx`.
 * The scale is the root font size over the browser's 16px: 0.9 on a laptop, 1 on her phone and in tests.
 */

/** The browser's default root size: 1rem at a scale of 1. */
const ROOT_PX = 16;

/** The root font size in px now (16 when it can't be read). */
function rootFontPx(): number {
  if (typeof document === 'undefined' || typeof getComputedStyle !== 'function') return ROOT_PX;
  const px = Number.parseFloat(getComputedStyle(document.documentElement).fontSize);
  return Number.isFinite(px) && px > 0 ? px : ROOT_PX;
}

// The scale changes only when the window crosses the phone line (or the browser's own size changes), both of which
// fire `resize`; it is read once per resize, not on every render.
let lastScale: number | null = null;
const scaleListeners = new Set<() => void>();
function rereadScale() {
  const next = rootFontPx() / ROOT_PX;
  if (next === lastScale) return;
  lastScale = next;
  for (const listener of scaleListeners) listener();
}
function subscribeScale(onChange: () => void): () => void {
  if (scaleListeners.size === 0) {
    lastScale = rootFontPx() / ROOT_PX;
    window.addEventListener('resize', rereadScale);
  }
  scaleListeners.add(onChange);
  return () => {
    scaleListeners.delete(onChange);
    if (scaleListeners.size === 0) {
      window.removeEventListener('resize', rereadScale);
      lastScale = null;
    }
  };
}
const scaleNow = () => lastScale ?? rootFontPx() / ROOT_PX;

/** The product's scale (root font size ÷ 16): 0.9 on a laptop, 1 on her phone. Re-renders when it changes. */
export function useRootScale(): number {
  return useSyncExternalStore(subscribeScale, scaleNow, () => 1);
}

/**
 * A design length written in px, as a CSS length that follows the product's scale by itself, the way the build writes
 * CSS (scripts/px-to-rem.mjs): `rem(32)` → "2rem" (28.8px on a laptop), and under 2px it stays px (`rem(1.5)` → "1.5px").
 * For a size JS only sets (a face's width, a tile's glyph, a chart's height); JS that computes with the length uses
 * `scaled(px, useRootScale())` instead.
 */
export function rem(px: number): string {
  return Math.abs(px) < 2 ? `${px}px` : `${Number((px / ROOT_PX).toFixed(5))}rem`;
}

/**
 * A CSS value written in JS with its px lengths in rem, as `rem` writes one length: a grid template
 * (`remLengths('minmax(260px,1.6fr) 128px')` → "minmax(16.25rem,1.6fr) 8rem"), a `calc()`, a shadow. Under 2px stays px.
 */
export function remLengths(value: string): string {
  return value.replace(/(?<![\w.-])(-?\d*\.?\d+)px\b/g, (whole, n: string) => (Math.abs(Number(n)) < 2 ? whole : rem(Number(n))));
}

/** A design length written in px, at the product's scale: `scaled(84, 0.9)` → 75.6. */
export function scaled(px: number, scale: number): number {
  return Math.round(px * scale * 100) / 100;
}

/**
 * A CSS length as read from a style (`getPropertyValue('--dw-top-room')`) in px: "5.75rem" through the root size,
 * "92px" or "92" as written. Anything else (a `calc()`, `em`, `%`, nothing) is NaN, as `parseFloat` gave, so a caller
 * keeps its fallback (`cssLengthPx(v) || 92`).
 */
export function cssLengthPx(value: string): number {
  const m = /^\s*(-?(?:\d+\.?\d*|\.\d+))(px|rem)?\s*$/i.exec(value);
  if (!m) return Number.NaN;
  const n = Number(m[1]);
  return m[2]?.toLowerCase() === 'rem' ? n * rootFontPx() : n;
}
